// setColumnIdentity — DdlEdit builders + commit dispatchers for the
// identity / auto-increment / SERIAL / GENERATED-IDENTITY surface (DSE2 §3.A2).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice A2.
//
// Two ops fold into this module:
//   • add-column with `column.identity?: IdentitySpec` — the full identity
//     spec (mode, start, increment, cycle) wired into a CREATE TABLE source
//     column-list insert or an ALTER TABLE ADD COLUMN clause. Supported on
//     every engine; DSE2's per-engine synthesizer translates to the dialect's
//     idiom.
//   • alter-column with `change.identity?: IdentityChange` — RESTART WITH n.
//     DSE2 only supports postgres for this op; mssql / mysql / oracle / sqlite
//     route to alternate paths (DBCC CHECKIDENT / table-level AUTO_INCREMENT
//     option / ALTER SEQUENCE / sqliteRecreate) and DSE2 throws with a
//     documented hint. The UI gates with `isIdentityRestartSupported(engine)`
//     and surfaces `getIdentityRestartUnsupportedTooltip(engine)` instead of
//     attempting a commit and surfacing a runtime error.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type {
  ColumnDef,
  DdlEdit,
  IdentitySpec,
} from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

// ─── add-column with identity ─────────────────────────────────────────────

export interface AddColumnWithIdentityArgs {
  readonly column: ColumnDef;
  readonly afterColumn?: string;
}

export function buildAddColumnWithIdentityEdit(args: AddColumnWithIdentityArgs): DdlEdit {
  return {
    op: 'add-column',
    column: args.column,
    afterColumn: args.afterColumn,
  };
}

export async function commitAddColumnWithIdentity(
  ctx: VqeDdlEditContext,
  args: AddColumnWithIdentityArgs,
): Promise<void> {
  const edit = buildAddColumnWithIdentityEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

// ─── alter-column RESTART WITH n ──────────────────────────────────────────

export interface AlterColumnIdentityRestartArgs {
  readonly columnName: string;
  readonly start: number;
}

export function buildAlterColumnIdentityRestartEdit(
  args: AlterColumnIdentityRestartArgs,
): DdlEdit {
  return {
    op: 'alter-column',
    columnName: args.columnName,
    change: { identity: { kind: 'restart', start: args.start } },
  };
}

export async function commitAlterColumnIdentityRestart(
  ctx: VqeDdlEditContext,
  args: AlterColumnIdentityRestartArgs,
): Promise<void> {
  const edit = buildAlterColumnIdentityRestartEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

// ─── per-engine support + redirect tooltips ───────────────────────────────

/** RESTART WITH n via `applyDdlEdit({ op: 'alter-column', change.identity })`
 *  is only synthesizable on postgres in DSE2 §3.A2. Every other engine routes
 *  to an alternate path; the UI consults this gate and surfaces the
 *  redirect tooltip instead of attempting the commit. */
export function isIdentityRestartSupported(engine: SqlEngine): boolean {
  return engine === 'postgres';
}

/** Per-engine redirect tooltip for restart-via-DDL. mssql → DBCC CHECKIDENT
 *  on the query path; mysql → `ALTER TABLE … AUTO_INCREMENT = N` on the query
 *  path; oracle → ALTER SEQUENCE on the underlying sequence; sqlite → the
 *  `sqlite_sequence` counter row on the query path. Each redirect names the
 *  same alternative the DSE2 synthesizer's rejection hint points at, so UI
 *  and runtime guidance stay consistent. */
export function getIdentityRestartUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mssql') {
    return (
      'MSSQL does not restart identity via ALTER COLUMN. Use the query '
      + 'editor’s Run + DBCC CHECKIDENT command.'
    );
  }
  if (engine === 'mysql') {
    return (
      'MySQL does not restart AUTO_INCREMENT via ALTER COLUMN. Run '
      + 'ALTER TABLE … AUTO_INCREMENT = N from the query editor instead.'
    );
  }
  if (engine === 'oracle') {
    return (
      'Oracle does not restart identity via ALTER COLUMN. Alter the underlying '
      + 'sequence directly via ALTER SEQUENCE on the query path.'
    );
  }
  if (engine === 'sqlite') {
    return (
      'SQLite does not restart AUTOINCREMENT via DDL. Update the table’s '
      + 'counter row in sqlite_sequence from the query editor instead.'
    );
  }
  return '';
}

// ─── per-engine validation for IdentitySpec on add-column ────────────────

/** Pre-emit validation mirroring DSE2's per-engine `formatIdentitySpec` rules.
 *  Returns null when the spec is acceptable for the engine; returns a human
 *  message when it would throw. The form uses this to disable Submit before
 *  the user attempts a doomed commit. */
export function validateIdentitySpecForEngine(
  engine: SqlEngine,
  spec: IdentitySpec,
): string | null {
  if (engine === 'mssql') {
    if (spec.mode === 'by-default-on-null') {
      return 'MSSQL IDENTITY does not accept BY DEFAULT ON NULL (Oracle-only).';
    }
    if (spec.cycle !== undefined) {
      return 'MSSQL IDENTITY does not accept a CYCLE option.';
    }
    const startSet = spec.start !== undefined;
    const incrSet = spec.increment !== undefined;
    if (startSet !== incrSet) {
      return 'MSSQL IDENTITY(start, increment) requires both fields, or neither.';
    }
    return null;
  }
  if (engine === 'postgres') {
    if (spec.mode === 'by-default-on-null') {
      return 'PostgreSQL GENERATED AS IDENTITY does not accept BY DEFAULT ON NULL (Oracle-only).';
    }
    return null;
  }
  if (engine === 'mysql') {
    // mysql column-level only emits AUTO_INCREMENT — no fields are honored on
    // the column emit. Sequence options live at the table level (slice E3).
    return null;
  }
  if (engine === 'oracle') {
    return null;
  }
  // sqlite — only the bare AUTOINCREMENT keyword. Modes + sequence options
  // are rejected by DSE2.
  if (spec.mode !== undefined) {
    return 'SQLite AUTOINCREMENT does not accept generation modes.';
  }
  if (spec.cycle !== undefined || spec.start !== undefined || spec.increment !== undefined) {
    return 'SQLite AUTOINCREMENT does not accept sequence options (start / increment / cycle).';
  }
  return null;
}

/** True when the supplied `IdentitySpec` is non-empty — at least one of
 *  `mode`, `start`, `increment`, `cycle` is set. Used by the form to enable
 *  Submit. The bare `{}` shape is still accepted by DSE2 (emits the bare
 *  IDENTITY / AUTO_INCREMENT / AUTOINCREMENT keyword), but the form treats
 *  the all-empty case as "user has not chosen anything yet" and keeps Submit
 *  disabled. */
export function isNonEmptyIdentitySpec(spec: IdentitySpec): boolean {
  return (
    spec.mode !== undefined
    || spec.start !== undefined
    || spec.increment !== undefined
    || spec.cycle !== undefined
  );
}
